import React, { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { COMPANY, MEDIA } from "@/lib/company";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

export default function ContactPage() {
  const [sent, setSent] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    toast.success("Message sent — our team will reach out shortly.");
  };
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <Navbar />
      <section className="relative overflow-hidden">
        <img src={MEDIA.courierWalk} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/92 via-slate-950/88 to-[#0B0F17]" />
        <div className="relative mx-auto max-w-5xl px-4 py-20 sm:px-6">
          <p className="mb-3 text-xs font-mono uppercase tracking-[0.25em] text-orange-400">Contact Us</p>
          <h1 className="text-4xl font-black tracking-tight sm:text-5xl">We're here to help.</h1>
          <p className="mt-4 max-w-xl text-base text-slate-300">
            Questions about a shipment, partnerships or careers? Reach {COMPANY.name} directly.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-14 sm:px-6 lg:grid-cols-2">
        <div className="space-y-4">
          <ContactRow icon={MapPin} label="Location" value={COMPANY.country} />
          <ContactRow icon={Phone} label="Phone" value={COMPANY.phone} href={`tel:${COMPANY.phone}`} testid="contact-phone" />
          <ContactRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-email" />
          <ContactRow icon={Clock} label="Support Hours" value="24 / 7 — every day of the year" />
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
          <h2 className="text-xl font-bold">Send us a message</h2>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
              <Input required data-testid="contact-name-input" className="border-white/10 bg-slate-800" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
              <Input required type="email" data-testid="contact-email-input" className="border-white/10 bg-slate-800" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-400">Message</Label>
              <Textarea required data-testid="contact-message-input" className="border-white/10 bg-slate-800" />
            </div>
            <Button type="submit" disabled={sent} data-testid="contact-submit" className="w-full bg-orange-600 text-white hover:bg-orange-700">
              {sent ? "Message Sent" : "Send Message"}
            </Button>
          </div>
        </form>
      </section>
      <Footer />
    </div>
  );
}

const ContactRow = ({ icon: Icon, label, value, href, testid }) => (
  <div className="flex items-start gap-4 rounded-xl border border-white/10 bg-slate-900/50 p-5">
    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-orange-600/15 text-orange-400">
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</p>
      {href ? (
        <a href={href} data-testid={testid} className="text-lg font-bold text-white hover:text-orange-300">{value}</a>
      ) : (
        <p className="text-lg font-bold text-white">{value}</p>
      )}
    </div>
  </div>
);
